import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import useI18n from '@/hooks/useI18n';
import { FormDataForCreateCampaign } from '@/types/interfaces';
import { Button } from '../Button';
import CustomRadioButton from '../CustomRadioButton';
import { capitalizeFirstLetter } from '@/utils/caseUtils';
import Tooltip from '../ToolTip';
import ToolTip from '../ToolTip';
const MAX_LIMIT_PER_PERSON = 5;
const DEFAULT_CONDITIONS_COUNT = 4;

interface CreateCampaignStep2Props {
  formData: FormDataForCreateCampaign;
  handleChange: (newValues: Partial<FormDataForCreateCampaign>) => void;
  onNext: () => void;
  onPrevious: () => void;
  availableBalance?: number;
  originalBudget?: number;
}

const CreateCampaignStep2: React.FC<CreateCampaignStep2Props> = ({
  formData,
  handleChange,
  onNext,
  onPrevious,
  availableBalance,
  originalBudget = 0,
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isFocusedLink, setIsFocusedLink] = useState<boolean>(false);
  const [newCondition, setNewCondition] = useState<string>('');
  const [budgetInput, setBudgetInput] = useState<string>('');
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormDataForCreateCampaign, string>>
  >({});
  const budgetRef = useRef<HTMLInputElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const conditionRef = useRef<HTMLInputElement>(null);
  const termsScrollRef = useRef<HTMLDivElement>(null);
  const termsThumbRef = useRef<HTMLDivElement>(null);
  const originalBudgetRef = useRef<number>(originalBudget);
  const prevConditionsLenRef = useRef<number>(
    (formData.conditions || []).length,
  );
  const didInitTermsScrollRef = useRef(false);
  const [isTermsScrollable, setIsTermsScrollable] = useState(false);
  const { t } = useI18n();

  const validationSchema = Yup.object({
    budget: Yup.number()
      .typeError(String(t('common.mandatoryField')))
      .required(String(t('common.mandatoryField')))
      .moreThan(99, String(t('common.minimumBudgetMustBe€100')))
      .test(
        'max-budget',
        String(t('common.budgetExceedsLimit')),
        function (value) {
          const balance = availableBalance || 0;
          const effectiveAvailable = balance + originalBudgetRef.current;
          return value <= effectiveAvailable;
        },
      ),
    smartLink: Yup.string().when('validFor', {
      is: (validFor: string[] = []) => validFor[0] !== 'store',
      then: (schema) =>
        schema
          .url(String(t('common.urlValidation')))
          .required(String(t('common.mandatoryField'))),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    validFor: Yup.array()
      .of(Yup.string())
      .min(0)
      .required(String(t('common.mandatoryField'))),
    limitPerPerson: Yup.number()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value,
      )
      .min(0, String(t('common.minLimitPerPerson')))
      .max(MAX_LIMIT_PER_PERSON, String(t('common.maxLimitPerPersonExceeded')))
      .required(String(t('common.mandatoryField'))),
    conditions: Yup.array().min(1, String(t('common.mandatoryField'))),
  });
  const validForOptions = ['online', 'store', 'both'];

  const handleRemoveConditions = (index: number) => {
    if (index < DEFAULT_CONDITIONS_COUNT) return;

    const updatedConditions = [...(formData.conditions || [])];
    updatedConditions.splice(index, 1);
    handleChange({ conditions: updatedConditions });
  };

  useEffect(() => {
    if (
      formData.budget !== undefined &&
      formData.budget !== null &&
      !Number.isNaN(formData.budget)
    ) {
      setBudgetInput(String(formData.budget));
    }
  }, [formData.budget]);
  useEffect(() => {
    const container = termsScrollRef.current;
    if (!container) return;

    const currentLen = (formData.conditions || []).length;
    const prevLen = prevConditionsLenRef.current;

    // If a condition was added, auto-scroll to bottom
    if (currentLen > prevLen) {
      // wait for DOM paint
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }

    prevConditionsLenRef.current = currentLen;
  }, [formData.conditions]);

  useEffect(() => {
    if (!formData.validFor || formData.validFor.length === 0) {
      handleChange({ validFor: ['online'] });
    }
  }, [formData.validFor, handleChange]);
  useEffect(() => {
    const container = termsScrollRef.current;
    if (!container) return;
    if (
      !didInitTermsScrollRef.current &&
      (formData.conditions || []).length > 0
    ) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      didInitTermsScrollRef.current = true;
    }
  }, [formData.conditions]);
  useEffect(() => {
    const container = termsScrollRef.current;
    if (!container) return;

    const checkScrollable = () => {
      setIsTermsScrollable(container.scrollHeight > container.clientHeight);
    };

    const onScroll = () => {
      const thumb = termsThumbRef.current;
      if (!thumb) return;

      const maxScroll = container.scrollHeight - container.clientHeight;
      const ratio = maxScroll > 0 ? container.scrollTop / maxScroll : 0;

      const maxTranslate = container.clientHeight - thumb.offsetHeight;
      thumb.style.transform = `translateY(${ratio * maxTranslate}px)`;
    };

    checkScrollable();
    onScroll();

    container.addEventListener('scroll', onScroll);

    const resizeObserver = new ResizeObserver(() => {
      checkScrollable();
      onScroll();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
    };
  }, [formData.conditions]);

  useEffect(() => {
    if (!formData.conditions || formData.conditions.length === 0) {
      handleChange({
        conditions: [
          t('common.conditions1'),
          t('common.conditions2'),
          t('common.conditions3'),
          t('common.conditions4'),
        ],
      });
    }
  }, []);

  const handleLimitChange = (value: string) => {
    if (value === '') {
      handleChange({ limitPerPerson: '' as any });
      return;
    }

    if (!/^\d+$/.test(value)) return;

    const numericValue = Number(value);

    if (numericValue > MAX_LIMIT_PER_PERSON) {
      setErrors((prev) => ({
        ...prev,
        limitPerPerson: String(t('common.maxLimitPerPersonExceeded')),
      }));
      return;
    }

    if (numericValue < 0) return;

    setErrors((prev) => ({ ...prev, limitPerPerson: undefined }));
    handleChange({ limitPerPerson: numericValue });
  };

  const decrement = () => {
    if (formData.limitPerPerson <= 0) return;

    setErrors((prev) => ({ ...prev, limitPerPerson: undefined }));
    handleChange({ limitPerPerson: formData.limitPerPerson - 1 });
  };

  const increment = () => {
    const current = Number(formData.limitPerPerson) || 0;
    const nextValue = current + 1;

    if (nextValue > MAX_LIMIT_PER_PERSON) {
      setErrors((prev) => ({
        ...prev,
        limitPerPerson: String(t('common.maxLimitPerPersonExceeded')),
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, limitPerPerson: undefined }));
    handleChange({ limitPerPerson: nextValue });
  };

  const handleAddCondition = () => {
    const trimmed = newCondition.trim();
    if (trimmed) {
      const currentConditions = formData.conditions || [];
      handleChange({ conditions: [...currentConditions, trimmed] });
      setNewCondition('');
      conditionRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCondition();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleAddCondition();
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      onNext();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Partial<
          Record<keyof FormDataForCreateCampaign, string>
        > = {};
        let hasMinBudgetError = false;
        let hasBudgetExceedsError = false;
        // When only one field fails, Yup puts the error in `err` itself and
        // leaves `err.inner` empty. Fall back to treating `err` as the sole error.
        const errorsToProcess = err.inner.length > 0 ? err.inner : [err];
        errorsToProcess.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof FormDataForCreateCampaign] =
              error.message;
          }
          if (error.message === String(t('common.minimumBudgetMustBe€100'))) {
            hasMinBudgetError = true;
          }
          if (error.message === String(t('common.budgetExceedsLimit'))) {
            hasBudgetExceedsError = true;
          }
        });
        setErrors(newErrors);
        if (hasMinBudgetError) {
          toast.error(String(t('common.minimumBudgetMustBeHundred')));
        } else if (hasBudgetExceedsError) {
          toast.error(String(t('common.budgetExceedsLimit')));
        } else {
          toast.error(String(t('campaign.errors.fixErrorsBeforeProceeding')));
        }
      }
    }
  };

  const handleValidForChange = (option: string) => {
    const updates: Partial<FormDataForCreateCampaign> = { validFor: [option] };
    if (option === 'store') {
      updates.smartLink = '';
    }
    handleChange(updates);
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      className="flex flex-col flex-1 min-h-0"
    >
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
        <h3 className="text-base font-semibold text-[#172B4D] mb-8">
          {t('common.cashbackSettings')}
        </h3>
        <div className="flex flex-row space-x-6">
          <div className="w-2/5 flex flex-col gap-4">
            <div>
              <div className="relative w-full">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={budgetInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setBudgetInput('');
                      handleChange({ budget: undefined });
                      return;
                    }
                    if (!/^\d+$/.test(value)) return;
                    if (value.length > 1 && value.startsWith('0')) return;
                    setBudgetInput(value);
                    handleChange({ budget: Number(value) });
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={
                    isFocused ? '' : String(t('common.campaignBudget'))
                  }
                  className="block w-full border border-[#B3B3B2] py-3 px-4
                  [appearance:textfield]
                  placeholder-[#B3B3B2]
                  rounded-[32px]
                  bg-white
                  outline-none
                  focus:border
                  focus:border-[#113E53]
                  text-[#113E53]
                  text-base
                  leading-[22px]"
                />
                {!isFocused && budgetInput === '' && (
                  <div className="absolute left-[150px] top-[12px] text-sm text-gray-400 pointer-events-auto">
                    <Tooltip
                      text={String(t('common.totalBudgetForThisCampaign'))}
                      position="top"
                    >
                      <span className="text-[#B3B3B2] text-base">ⓘ</span>
                    </Tooltip>
                  </div>
                )}
                <span className="absolute  right-5 top-1/2 -translate-y-1/2 text-[#B3B3B2] text-base pointer-events-none text-lg">
                  €
                </span>
              </div>
              {errors.budget && (
                <p className="text-sm text-red-500">{errors.budget}</p>
              )}
            </div>
            <div>
              <label className="text-[#172B4D] text-base">
                {t('common.validFor')}
              </label>
              <div className="flex space-x-4 mt-2 ml-2">
                {validForOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <CustomRadioButton
                      name="validFor"
                      value={option}
                      checked={option === formData.validFor[0]}
                      onChange={() => handleValidForChange(option)}
                      label={capitalizeFirstLetter(option)}
                    />
                  </div>
                ))}
              </div>
              {errors.validFor && (
                <p className="text-sm text-red-500">{errors.validFor}</p>
              )}
            </div>
            {formData.validFor[0] !== 'store' && (
              <div>
                <div className="relative w-full">
                  <input
                    ref={linkRef}
                    name="smartLink"
                    onFocus={() => setIsFocusedLink(true)}
                    onBlur={() => setIsFocusedLink(false)}
                    onClick={() => {
                      linkRef.current?.focus();
                      setIsFocusedLink(true);
                    }}
                    type="text"
                    value={formData?.smartLink || ''}
                    onChange={(e) =>
                      handleChange({ smartLink: e.target.value })
                    }
                    className="block w-full border border-[#B3B3B2] py-3 px-4  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder-[#B3B3B2] overflow-ellipsis whitespace-nowrap rounded-[32px] bg-white disabled:border-[#B3B3B2]  disabled:bg-[#F4F4F4] disabled:text-[#B3B3B2] outline-none focus:border focus:border-[#113E53] text-[#113E53]  text-base leading-[22px]"
                    aria-label="smartLink"
                    placeholder={
                      isFocusedLink ? '' : String(t('common.YourBrandWebsite'))
                    }
                  />
                  {!formData?.smartLink && !isFocusedLink && (
                    <div className="absolute left-[158px] top-[12px] text-sm text-gray-400 pointer-events-auto">
                      <Tooltip
                        text={String(t('common.onlineStoreLink'))}
                        position="top"
                      >
                        <span className="text-[#B3B3B2] text-base">ⓘ</span>
                      </Tooltip>
                    </div>
                  )}
                </div>
                {errors.smartLink && (
                  <p className="text-sm text-red-500">{errors.smartLink}</p>
                )}
              </div>
            )}

            <div>
              <label className="text-[#172B4D] text-base">
                {t('common.limitPerPerson')}
              </label>
              <div className="flex w-min border border-[#B3B3B2] rounded-full p-2 items-center justify-between mt-2 gap-[5px]">
                <img
                  src="/assets/svgs/fi_minus.svg"
                  className="h-[20px] w-[20px] hover:bg-gray-200 rounded-full"
                  onClick={decrement}
                />
                <input
                  type="number"
                  min={0}
                  max={MAX_LIMIT_PER_PERSON}
                  value={formData.limitPerPerson}
                  onChange={(e) => handleLimitChange(e.target.value)}
                  className="w-[80px] text-center bg-transparent outline-none text-[#4D494F] text-base [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <img
                  src="/assets/svgs/plus.svg"
                  className="h-[20px] w-[20px] hover:bg-gray-200 rounded-full"
                  onClick={increment}
                />
              </div>
              {errors.limitPerPerson && (
                <p className="text-sm text-red-500">{errors.limitPerPerson}</p>
              )}
            </div>
          </div>
          <div className="w-3/5">
            <div className="border border-[#B3B3B2] rounded-3xl p-4">
              <div className="flex items-center mb-2">
                <h4 className="text-base font-normal text-[#B3B3B2]">
                  {t('common.termsConditions')}{' '}
                  <ToolTip
                    text={String(t(`common.termsConditionsTooltip`))}
                    position="top"
                  >
                    <span>ⓘ</span>
                  </ToolTip>
                </h4>
              </div>
              <div className="bg-[#FAFAFA] rounded-2xl px-4 py-3 relative">
                <div
                  ref={termsScrollRef}
                  className="terms-scroll max-h-[140px] overflow-y-auto pr-5"
                >
                  {(formData.conditions || []).map((condition, index) => (
                    <div
                      key={index}
                      className="flex items-start justify-between gap-3 text-base text-[#4D4D4D]"
                    >
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <span className="leading-[22px] font-normal flex-shrink-0 min-w-[18px] text-right">
                          {index + 1}.
                        </span>
                        <span className="leading-[22px] font-normal break-words whitespace-normal min-w-0">
                          {condition}
                        </span>
                      </div>

                      {index >= DEFAULT_CONDITIONS_COUNT && (
                        <button
                          onClick={() => handleRemoveConditions(index)}
                          className="ml-1 text-gray-600 hover:text-gray-800 flex-shrink-0 w-6 h-6 flex items-center justify-center"
                          aria-label={`Remove condition ${condition}`}
                          type="button"
                        >
                          <img
                            src="/assets/fi_close_black.svg"
                            alt={String(t('campaign.altTexts.remove'))}
                            className="h-[16px] w-[16px]"
                          />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Fake scrollbar like Step4 */}
                {isTermsScrollable && (
                  <div className="terms-fake-track">
                    <div ref={termsThumbRef} className="terms-fake-thumb" />
                  </div>
                )}

                <style>
                  {`
      .terms-scroll {
        scrollbar-width: none; /* Firefox */
      }
      .terms-scroll::-webkit-scrollbar {
        display: none; /* Chrome / Edge / Safari */
      }

      .terms-fake-track {
        position: absolute;
        top: 12px;
        right: 10px;
        width: 4px;
        height: calc(140px - 24px);
        pointer-events: none;
      }

      .terms-fake-thumb {
        width: 100%;
        height: 40px;
        background-color: #C7CCD1;
        border-radius: 9999px;
        transform: translateY(0);
        transition: transform 0.1s linear;
      }
    `}
                </style>
              </div>
              <div className="mt-4">
                <input
                  ref={conditionRef}
                  type="text"
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={String(
                    t('campaign.placeholders.typeOtherConditions'),
                  )}
                  className="block w-full p-2 pt-0 text-sm text-[#172B4D] placeholder-[#B3B3B2] focus:border-none focus:outline-none focus:ring-0"
                />
              </div>
            </div>
            {errors.conditions && (
              <p className="text-sm text-red-500">{errors.conditions}</p>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white flex justify-end w-full shrink-0">
        <div className="flex gap-4 w-full p-6 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={onPrevious}
            className="w-[122px]"
          >
            {t('common.previous')}
          </Button>
          <Button type="submit" className="w-[122px]">
            {t('common.next')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateCampaignStep2;
